import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { userApi } from '../lib/api'
import LoadingSpinner from './LoadingSpinner'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import SyntaxHighlighter from './SyntaxHighlighter'

const LiveSessionView: React.FC = () => {
  // Get latest result - always fetch and refresh automatically
  const { data: resultsData, isLoading: resultsLoading } = useQuery({
    queryKey: ['results', 1, 1],
    queryFn: () => userApi.getResults(1, 1),
    refetchInterval: 2000, // Refetch every 2 seconds for live updates
    refetchIntervalInBackground: false,
  })

  const latestResult = resultsData?.results?.[0]

  const renderResultDetailView = () => {
    if (resultsLoading) {
      return <LoadingSpinner />
    }

    if (!latestResult) {
      return (
        <div className="flex flex-col items-center justify-center h-full min-h-96 text-center">
          <div className="max-w-md mx-auto">
            <div className="mb-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Results Yet</h3>
              <p className="text-gray-600 mb-4">
                Take your first screenshot to see AI analysis results here.
              </p>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Quick Start</h4>
              <div className="text-sm text-blue-800 space-y-1">
                <p><kbd className="px-2 py-1 bg-blue-100 rounded text-xs font-mono">CTRL+SHIFT+8</kbd> Single screenshot</p>
                <p><kbd className="px-2 py-1 bg-blue-100 rounded text-xs font-mono">CTRL+SHIFT+5</kbd> Start multi-screenshot session</p>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="h-full flex flex-col bg-white">
        {/* Minimal Result Header */}
        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              {latestResult.result_type === 'session' && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  SESSION
                </span>
              )}
              <span className="text-sm text-gray-600">
                {new Date(latestResult.created_at).toLocaleString()}
              </span>
              <span className="text-xs text-gray-500">
                {latestResult.processing_time_ms}ms
              </span>
            </div>
          </div>
        </div>

        {/* Full-Screen Result Content */}
        <div className="flex-1 overflow-auto p-6 text-left">
          <div className="prose max-w-4xl mx-auto">
            <ReactMarkdown
              children={latestResult.llm_output_text}
              remarkPlugins={[remarkGfm]}
              components={{
                code(props) {
                  const { children, className, node, ...rest } = props
                  const match = /language-(\w+)/.exec(className || '')
                  return match
                    ? <SyntaxHighlighter language={match[1]}>{String(children).replace(/\n$/, '')}</SyntaxHighlighter>
                    : <code {...rest} className={className}>{children}</code>
                }
              }}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Simple Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold text-gray-900">Latest Result</h1>
          <a
            href="/results"
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            View All Results
          </a>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        {renderResultDetailView()}
      </div>
    </div>
  )
}

export default LiveSessionView